<template>
  <div class="guest-summary">
    <div class="summary-header d-flex align-center mb-2 flex-wrap">
      <div class="main-title mr-6">{{ $t("device_status_header") }}</div>
    </div>
    <div v-if="!isLoaded && !devices.length" class="content-wrapper">
      <v-skeleton-loader v-for="i in 6" :key="i" class="skeleton" type="image" height="150px"  />
    </div>
    <div v-else class="content-wrapper">
      <div v-for="device in devices" :key="device.deviceId" class="device">
        <div class="device-icon">
          <img class="icon" :src="icons[device.deviceType]" :alt="device.deviceType" width="22" height="22">
        </div>
        <h2 class="device-name">
          {{ $t(`device_name.${device.deviceType}`) }}
          <span v-if="device.deviceTags.length" class="text-muted">({{ getLabel(device.deviceTags) }})</span>
        </h2>
        <template v-if="device.deviceType === 'octopus_report'">
          <p
            class="status"
            :class="$moment(device?.details?.last_upload) > $moment().subtract(1, 'day') ? 'operational' : 'error'"
          >
            {{ $moment(device?.details?.last_upload).format('YYYY-MM-DD HH:mm') }}
          </p>
        </template>
        <template v-else>
          <p
            class="status"
            :class="device.isOnline ? 'operational' : 'error'"
          >
            {{ $t(`device_status.${device.isOnline ? 'online' : 'offline'}`) }}
          </p>
          <template v-if="device.deviceType === 'printer'">
            <p v-if="device?.details?.status === PRINTER_STATUS.PAPER_OK" class="status operational">
              {{ $t('printer_status.paper_ok') }}
            </p>
            <p v-else-if="device?.details?.status === PRINTER_STATUS.PAPER_LOW" class="status warning">
              {{ $t('printer_status.paper_low') }}
            </p>
            <p v-else-if="device?.details?.status === PRINTER_STATUS.OUT_OF_PAPER" class="status error">
              {{ $t('printer_status.out_of_paper') }}
            </p>
            <span v-if="device?.details?.last_out_of_paper" class="note">
            {{ $t('last_out_of_paper', { date: $moment(device?.details?.last_out_of_paper).format('YYYY-MM-DD') }) }}
          </span>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {base} from "@/axios-api";
import qs from "qs";

export default {
  name: "Devices",
  props: {
    place: {
      default: 0,
    },
  },
  data() {
    return {
      devices: [],
      isLoaded: false,
      icons: {
        camera: require('@/assets/images/devices/camera.svg'),
        gate: require('@/assets/images/devices/gate.svg'),
        kiosk: require('@/assets/images/devices/kiosk.svg'),
        pos: require('@/assets/images/devices/pos.svg'),
        printer: require('@/assets/images/devices/printer.svg'),
        screen: require('@/assets/images/devices/screen.svg'),
        server: require('@/assets/images/devices/server.svg'),
        voip: require('@/assets/images/devices/voip.svg'),
        octopus_report: require('@/assets/images/devices/upload.svg'),
      },
      PRINTER_STATUS: {
        OUT_OF_PAPER: "out_of_paper",
        PAPER_LOW: "paper_low",
        PAPER_OK: "paper_ok",
      }
    }
  },
  methods: {
    getPrinterData() {
      base({
        url: '/uptime-monitor/statuses',
        method: 'GET',
        params: {
          place_id: this.place,
        },
        paramsSerializer: params => {
          return qs.stringify(params, {
            arrayFormat: 'repeat'
          })
        },
      })
          .then((response) => {
            console.log(response)
            this.isLoaded = true;
            this.devices = response.data.devices;
          });
    },
    getLabel(tags) {
      return tags.map((tag) => this.$te(`location_type.${tag}`) ? this.$t(`location_type.${tag}`) : tag).join(' - ');
    },
    handlePublication(push) {
      if (push.data.event === 'device_status_update') {
        this.devices = push.data.content;
      }
    },
  },
  created() {
    this.getPrinterData();
  },
}
</script>

<style scoped lang="scss">
.content-wrapper {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(195px, 1fr));
  grid-auto-rows: minmax(150px, auto);
  overflow-x: hidden;

  &:has(.device:nth-child(3)) {
    overflow-x: scroll;
  }
}

.skeleton {
  border-radius: 12px;
}

.device {
  display: flex;
  flex-direction: column;
  padding: 15px;

  border: 1px solid #f0f0f0;
  border-radius: 12px;
}

.device-icon {
  width: 50px;
  height: 50px;
  background-color: #fcfcfc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  margin-bottom: 12px;
}

.icon {
  width: 32px;
  height: 32px;
}

.device-name {
  font-size: 13px;
  margin-bottom: 4px;
}

.note {
  font-size: 12px;
  font-weight: 400;
}

.status {
  margin-top: 4px;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;

  & + .status {
    margin-top: 0;
  }

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    box-shadow: 0 0 4px;
  }

  &.operational {
    color: #00c853;
    &::before {
      background-color: #00c853;
      box-shadow: 0 0 4px #00c853;
    }
  }

  &.warning {
    color: #ffb600;
    &::before {
      background-color: #ffb600;
      box-shadow: 0 0 4px #ffb600;
    }
  }

  &.error {
    color: #ff5252;

    &::before {
      background-color: #ff5252;
      box-shadow: 0 0 4px #ff5252;
      animation: pulseOffline 1.5s ease-in-out infinite;
    }

    @keyframes pulseOffline {
      0% {
        transform: scale(1);
        box-shadow: 0 0 4px #ff5252;
        background-color: #f15454;
      }
      50% {
        transform: scale(1.2);
        box-shadow: 0 0 5px #ff5252;
        background-color: #ff5252;
      }
      100% {
        transform: scale(1);
        box-shadow: 0 0 4px #ff5252;
        background-color: #f15454;
      }
    }
  }
}
</style>
